import ky from "ky";
import authWrappers from "./auth";
import policies from "./policies";
import credentials from "@/lib/http/credentials";
import company from "@/lib/http/company";
import guardrails from "@/lib/http/guardrails";
import { env } from "@/env";
import { setOrganizationLocked } from "@/lib/billing-lock";
import { readBillingErrorResponse } from "@/lib/http/billing";

const refreshTokenStorageKey = "polygraf_refresh_token" as const;
const tempTokenMarkerKey = "temp-token" as const;

const AUTH_PUBLIC_PATHS = new Set([
  "/auth/login",
  "/auth/signup",
  "/auth/forgot-password",
  "/auth/verify",
  "/auth/verify-code",
  "/auth/guest-signup",
  "/auth/signin",
  "/login",
  "/signin",
]);

type TokenRefreshResponse = {
  access_token: string;
  refresh_token: string;
  token_type: "bearer";
};

function createClient() {
  // make the access_token variable non-global and close over it
  let accessToken: string | null = null;

  async function _refreshToken() {
    const refresh_token = localStorage.getItem(refreshTokenStorageKey);
    if (!refresh_token) return false;
    const tokensRes = await ky.post<TokenRefreshResponse>(
      "api/v1/auth/refresh",
      {
        baseUrl: env.VITE_AUTH_API_URL,
        json: {
          refresh_token,
        },
        throwHttpErrors: false,
      },
    );

    if (!tokensRes.ok) {
      // Only drop the token when the server actually rejected it. A 429 from
      // the endpoint's rate limit, or a 5xx, is temporary -- throwing away a
      // still-valid refresh token there logs the user out for nothing.
      if (tokensRes.status === 401 || tokensRes.status === 403) {
        localStorage.removeItem(refreshTokenStorageKey);
      }
      return false;
    }
    const tokens = await tokensRes.json();

    localStorage.setItem(refreshTokenStorageKey, tokens.refresh_token);
    accessToken = tokens.access_token;
    return true;
  }

  // coalesce refresh calls
  let refreshing: null | Promise<boolean> = null;
  function refreshToken() {
    if (!refreshing) {
      refreshing = _refreshToken();
      refreshing.finally(() => void (refreshing = null));
    }
    return refreshing;
  }

  void refreshToken();

  const baseClient = ky.create({
    hooks: {
      beforeRequest: [
        async ({ request }) => {
          if (accessToken === null) {
            const refreshed = await refreshToken();
            if (!refreshed) return;
          }
          request.headers.set("Authorization", `Bearer ${accessToken}`);
        },
      ],
      afterResponse: [
        async ({ response, request, options }) => {
          if (response.status === 402) {
            const billingError = await readBillingErrorResponse(response);
            if (billingError?.code === "ORGANIZATION_LOCKED") {
              setOrganizationLocked(true);
            }
          }

          if (response.status === 401) {
            // `refreshing` is cleared as soon as one refresh settles, so a
            // page that fires several requests at once would otherwise send a
            // separate refresh per 401 and burn through the rate limit. If the
            // token already moved on since this request went out, the 401 is
            // stale: just retry with the current token.
            const usedAuth = request.headers.get("Authorization");
            if (usedAuth === null || usedAuth === `Bearer ${accessToken}`) {
              await refreshToken();
            }
            request.headers.set("Authorization", `Bearer ${accessToken}`);

            // goes in an infinite loop if you call with `ky` for some reason
            return fetch(request, options).then((r) => {
              if (
                r.status === 401 &&
                localStorage.getItem(refreshTokenStorageKey) !== null &&
                !AUTH_PUBLIC_PATHS.has(window.location.pathname)
              ) {
                // force log out if it's still a 401
                localStorage.removeItem(refreshTokenStorageKey);
                window.location.pathname = "/auth/login";
              }
            });
          }
        },
      ],
    },
  });

  const authClient = baseClient.extend({
    baseUrl: import.meta.env.VITE_AUTH_API_URL,
  });
  const processingClient = baseClient.extend({
    baseUrl: import.meta.env.VITE_PROCESSING_API_URL,
  });
  const paymentClient = baseClient.extend({
    baseUrl: env.VITE_PAYMENT_API_URL,
  });

  function saveTokens(tokens: { access: string; refresh: string | null }) {
    accessToken = tokens.access;
    setOrganizationLocked(false);
    if (!accessToken && !refreshing) return false;
    if (tokens.refresh)
      localStorage.setItem(refreshTokenStorageKey, tokens.refresh);
    else {
      sessionStorage.setItem(tempTokenMarkerKey, "true");
    }
  }

  function logout() {
    accessToken = null;
    setOrganizationLocked(false);
    localStorage.removeItem(refreshTokenStorageKey);
    sessionStorage.removeItem(tempTokenMarkerKey);
  }

  function isLoggedIn(): boolean | "temp" {
    const refresh = localStorage.getItem(refreshTokenStorageKey);
    if (!refresh)
      return sessionStorage.getItem(tempTokenMarkerKey) === "true" && "temp";
    return Boolean(refresh);
  }

  return {
    clients: {
      auth: authClient,
      processing: processingClient,
      payment: paymentClient,
    },
    saveTokens,
    logout,
    isLoggedIn,
    _getAccessToken: async () => {
      if (accessToken == null) await refreshToken();
      return accessToken;
    },
    refreshAccessToken: refreshToken,
  };
}

const {
  clients,
  saveTokens,
  logout,
  isLoggedIn,
  _getAccessToken,
  refreshAccessToken,
} = createClient();
export { saveTokens, logout, isLoggedIn, refreshAccessToken };

/** @deprecated */
export const oldApi = {
  raw: clients.auth,
  auth: authWrappers,
  policies,
  credentials,
  company,
  guardrails,
} as const;

export const api = {
  /** @deprecated */
  // eslint-disable-next-line @typescript-eslint/no-deprecated
  raw: oldApi.raw,
  processing: {
    raw: clients.processing,
  },
  payment: {
    raw: clients.payment,
  },
  auth: {
    raw: clients.auth,
    ...authWrappers,
  },
};

(api as any).__gat = _getAccessToken;
